/**
 * KALINAZ — behaviour for the bugiz-* sections.
 *
 * <bz-hero>      Two synced videos ported from the AI Studio prototype: a full-bleed "sky flight"
 *                video and a transparent alpha drone overlay. Plays once, swaps to the sky video
 *                between skyStart/skyEnd, then the landed drone bobs in place.
 * <bz-carousel>  Scroll-snap product track with prev/next arrows.
 * <bz-header>    Mobile menu toggle.
 * .bz-reveal     Fade-up on scroll.
 */
(() => {
  if (window.__bugizLoaded) return;
  window.__bugizLoaded = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const bzT = (key, fallback) => (window.BZ_I18N && window.BZ_I18N[key]) || fallback;

  // Horizon scrolls .page-wrapper (not the window) on desktop; fall back to the window elsewhere.
  const getScroller = () => {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1 && /(auto|scroll)/.test(getComputedStyle(wrapper).overflowY)) {
      return wrapper;
    }
    return null;
  };
  const scrollState = () => {
    const el = getScroller();
    if (el) return { y: el.scrollTop, max: el.scrollHeight - el.clientHeight };
    return { y: window.scrollY, max: document.documentElement.scrollHeight - window.innerHeight };
  };

  /* ---------- Hero flight sequence (shared with the React build: src/lib/heroController.js) ---------- */
  /**
   * Safari / iOS does not support alpha-channel transparency in WebM (VP9).
   * Detect WebKit/Safari to route transparent playback through HTML5 Canvas
   * rendering pre-extracted transparent WebP frames.
   */
  function isSafariOrNoWebMAlpha() {
    if (typeof window === 'undefined') return false;
    const ua = navigator.userAgent;
    // All iOS browsers (Safari, Chrome for iOS, Firefox for iOS use Apple WebKit)
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isIOS) return true;

    // macOS Safari
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua) || (navigator.vendor && navigator.vendor.includes('Apple'));
    if (isSafari) return true;

    const testVideo = document.createElement('video');
    const canPlay = testVideo.canPlayType('video/webm; codecs="vp9"');
    if (!canPlay) return true;

    return false;
  }

  class HeroController {
    constructor(root) {
      this.root = root;
      this.sky = this.root.querySelector('[data-sky]');
      this.overlay = this.root.querySelector('[data-overlay]');
      this.canvas = this.root.querySelector('[data-overlay-canvas]');
      this.playBtn = this.root.querySelector('[data-play]');
      this.restartBtn = this.root.querySelector('[data-restart]');

      this.skyStart = parseFloat(this.root.dataset.skyStart) || 2.55;
      this.skyEnd = parseFloat(this.root.dataset.skyEnd) || 8.26;
      this.endTime = parseFloat(this.root.dataset.endTime) || 9.9;

      // Canvas fallback variables for Safari/iOS
      this.useCanvas = isSafariOrNoWebMAlpha() && !!this.canvas;
      this.canvasCtx = null;
      this.heroFrames = [];
      this.lastHeroFrameIdx = -1;

      // Refs rather than DOM reads keep the rAF tick cheap
      this.isPlaying = false;
      this.hasEnded = false;
      this.inSky = false;
      this.lastSyncCheck = 0;
      this.lastTelemetry = 0;
      // performance.now() when the sky clip ran out; the landing keeps going on its own clock after that
      this.skyEndedAt = 0;

      // HUD readouts (optional)
      this.tel = {};
      for (const el of this.root.querySelectorAll('[data-tel]')) this.tel[el.dataset.tel] = el;
      this.telBar = this.root.querySelector('[data-tel-bar="alt"]');

      if (!this.sky) return;

      if (this.useCanvas) {
        this.initCanvasFallback();
      }

      for (const video of [this.sky, this.overlay].filter(Boolean)) {
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
      }

      this.onEnded = () => this.finish();
      // The sky clip can be shorter than the full sequence (the portrait mobile clip ends at ~8.9s,
      // the landing runs to endTime). Don't stop everything when it ends — let tick() finish the landing.
      this.onSkyEnded = () => {
        if (!this.skyEndedAt) this.skyEndedAt = performance.now();
        this.startLoop();
      };
      this.sky.addEventListener('ended', this.onSkyEnded);
      if (this.overlay) {
        this.overlay.addEventListener('ended', this.onEnded);
        this.overlay.addEventListener('error', () => {
          if (!this.useCanvas && this.canvas) {
            this.initCanvasFallback();
          }
        });
      }

      this.playBtn?.addEventListener('click', () => this.togglePlayPause());
      this.restartBtn?.addEventListener('click', () => this.restart());

      // Autoplay can be blocked until the first interaction — retry once on any gesture
      this.onGesture = () => {
        const isPaused = this.useCanvas ? this.sky.paused : (this.sky.paused || this.overlay?.paused);
        if (isPaused && !this.hasEnded && !this.skyEndedAt && this.isPlaying && !this.buffering) this.start();
      };
      for (const type of ['click', 'touchstart', 'keydown']) {
        window.addEventListener(type, this.onGesture, { once: true, passive: true });
      }

      this.tick = this.tick.bind(this);
      this.running = false;
      this.inView = true;
      this.wasPlaying = false;

      // Lite mode: low hardware specs, data saver, slow connection, or reduced motion
      const connection = navigator.connection || {};
      const cores = navigator.hardwareConcurrency || 4;
      const memory = navigator.deviceMemory || 8;
      this.lite = reducedMotion || cores <= 2 || memory <= 2 || connection.saveData === true || /(^|-)2g$/.test(connection.effectiveType || '');
      this.mobile = window.matchMedia('(max-width: 899px)').matches;
      this.isFlying = false;

      // Sky → landed-drone handoff. Desktop is a straight cut at skyEnd, the moment the overlay drone flies into
      // the camera and fills the whole frame (~8.25–8.29s), so nothing of either clip shows through a fade — the
      // sky clip turns into a site mockup right after. The mobile clip has no such cut, so it gets a soft fade.
      this.skyFade = this.mobile ? 0.5 : 0;
      this.root.style.setProperty('--bz-sky-fade', `${this.skyFade}s`);

      // The mobile sky clip is its own portrait cut that runs through the second KALINAZ title to the drone
      // hovering in front of it. Show it right up to the end of the sequence (or the end of the file, if
      // shorter) instead of hiding it at the desktop clip's skyEnd; it then crossfades onto the landed drone.
      // Resolved from the clip's duration as soon as it is known — on loadedmetadata, and re-checked every
      // tick, because the event can come and go before we listen (Safari/iOS, hot reloads, a reused element),
      // which silently left phones on the desktop cut-off (8.26s).
      this.skyEndResolved = false;
      this.onSkyMeta = () => {
        const d = this.sky.duration;
        if (this.skyEndResolved || !Number.isFinite(d) || d <= 0) return;
        this.skyEndResolved = true;
        if (this.mobile && this.sky.dataset.srcMobile && d > this.skyEnd) {
          this.skyEnd = Math.min(d, this.endTime);
        }
      };
      this.sky.addEventListener('loadedmetadata', this.onSkyMeta);
      this.sky.addEventListener('durationchange', this.onSkyMeta);

      // Only run the rAF loop (and keep videos playing) while the hero is on screen and the tab is visible
      this.visibilityObserver = new IntersectionObserver(([entry]) => this.setInView(entry.isIntersecting), {
        threshold: 0.05,
      });
      this.visibilityObserver.observe(this.root);

      // Scrolling down during the intro finishes it and brings the menu back at once
      this.onPageScroll = () => {
        if (window.scrollY > 40 && this.isPlaying && !this.hasEnded) this.skipToEnd();
        else this.syncHeader();
      };
      window.addEventListener('scroll', this.onPageScroll, { passive: true });
      this.onTabChange = () => this.setInView(this.inView, true);
      document.addEventListener('visibilitychange', this.onTabChange);

      this.setState(this.lite ? 'paused' : 'playing');
      this.whenPageLoaded(() => this.boot());
    }

    /**
     * Configures HTML5 Canvas for Safari / iOS to draw 200 transparent WebP frames,
     * completely bypassing WebKit's broken WebM alpha rendering.
     */
    /** Theme asset URL of hero frame n (1-based), derived from data-frame-url (frame 001). */
    heroFrameUrl(n) {
      const pattern = this.root.dataset.frameUrl || '';
      return pattern.replace('hero-frame-001', `hero-frame-${String(n).padStart(3, '0')}`);
    }

    initCanvasFallback() {
      this.useCanvas = true;
      if (this.overlay) this.overlay.style.display = 'none';
      if (!this.canvas) return;

      this.canvas.style.display = 'block';
      this.canvasCtx = this.canvas.getContext('2d');
      this.canvas.width = 960;
      this.canvas.height = 540;

      this.heroFrames = new Array(200);

      // Frame 0 priority load for instant initial paint
      const firstImg = new Image();
      firstImg.src = this.heroFrameUrl(1);
      const onFirstLoad = () => {
        this.heroFrames[0] = firstImg;
        this.drawHeroCanvas(0);
      };
      if (firstImg.decode) {
        firstImg.decode().then(onFirstLoad).catch(onFirstLoad);
      } else {
        firstImg.onload = onFirstLoad;
      }

      // Load the remaining 199 frames in playback order, 8 at a time so they never swamp the network or the
      // decoder. They start right away (not after window load): the takeoff waits for its frames (whenBuffered),
      // otherwise a phone on a slow connection froze the drone on a missing frame, then jumped ahead.
      const TAKEOFF = Math.min(200, Math.ceil(this.skyStart * 20) + 1);
      let loaded = 0;
      this.takeoffFramesReady = new Promise((resolve) => {
        this.onTakeoffFrames = resolve;
      });
      let next = 1;
      let active = 0;
      const pump = () => {
        while (active < 8 && next < 200) {
          const i = next++;
          const img = new Image();
          active++;
          img.onload = img.onerror = () => {
            active--;
            if (i < TAKEOFF && ++loaded >= TAKEOFF - 1) this.onTakeoffFrames();
            // a frame the playhead already asked for arrived late: paint it now
            if (this.wantedHeroFrame === i) this.drawHeroCanvas(i / 20, true);
            pump();
          };
          img.src = this.heroFrameUrl(i + 1);
          this.heroFrames[i] = img;
        }
      };
      pump();
    }

    /**
     * Renders the transparent frame corresponding to flight time t (0s to 10s).
     */
    drawHeroCanvas(t, force = false) {
      if (!this.useCanvas || !this.canvasCtx) return;
      const TOTAL = 200;
      const FPS = 20; // frame_NNN.webp is the overlay clip at t = (NNN - 1) / 20s
      const frameIdx = Math.max(0, Math.min(TOTAL - 1, Math.round(t * FPS)));

      if (frameIdx === this.lastHeroFrameIdx && !force) return;
      this.wantedHeroFrame = frameIdx;

      // Missing frame (still downloading): keep showing the nearest earlier one, and don't mark this one as
      // drawn, so it is painted as soon as it lands instead of being skipped
      let i = frameIdx;
      const ready = (img) => img && img.complete && img.naturalWidth > 0;
      while (i > 0 && !ready(this.heroFrames[i])) i--;
      const img = this.heroFrames[i];
      if (i !== frameIdx && i === this.lastDrawnHeroFrame) return;
      if (i === frameIdx) this.lastHeroFrameIdx = frameIdx;
      if (ready(img)) {
        this.lastDrawnHeroFrame = i;
        this.canvasCtx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.canvasCtx.drawImage(img, 0, 0, this.canvas.width, this.canvas.height);
      }
    }

    /** Start video downloads once DOM is ready so initial frame displays immediately */
    whenPageLoaded(callback) {
      if (document.readyState === 'complete' || document.readyState === 'interactive') return callback();
      let done = false;
      const run = () => {
        if (done) return;
        done = true;
        callback();
      };
      window.addEventListener('DOMContentLoaded', run, { once: true });
      window.addEventListener('load', run, { once: true });
      setTimeout(run, 800);
    }

    srcFor(video) {
      if (this.mobile) return video.dataset.srcMobile || video.dataset.srcDesktop || '';
      // Retina laptops and big monitors get the 2560px sky clip, everything else the 1920px one
      const px = window.innerWidth * Math.min(window.devicePixelRatio || 1, 2);
      if (video.dataset.srcDesktopHd && px > 2000) return video.dataset.srcDesktopHd;
      return video.dataset.srcDesktop || '';
    }

    loadVideo(video) {
      if (video.getAttribute('src')) return;
      const src = this.srcFor(video);
      if (!src) return;
      video.preload = 'auto';
      video.src = src;
    }

    boot() {
      if (!this.useCanvas && this.overlay) {
        this.loadVideo(this.overlay);
      }

      if (this.lite) {
        // In lite mode, leave drone at frame 0 on the landing pad ready for takeoff
        if (this.useCanvas) {
          this.drawHeroCanvas(0);
        } else if (this.overlay) {
          this.overlay.currentTime = 0;
        }
        return;
      }

      this.loadVideo(this.sky);
      this.isPlaying = true;
      // Hold the takeoff — the drone rests on the pad (the overlay's poster) — until both clips have buffered
      // enough to play through, so a slow first visit doesn't stall mid-flight. Capped so it never waits long.
      this.buffering = true;
      this.whenBuffered().then(() => {
        this.buffering = false;
        if (this.hasEnded) return;
        if (window.scrollY > 40) return this.skipToEnd();
        this.startLoop();
        if (this.inView) this.start();
      });
    }

    whenBuffered() {
      const videos = [this.sky, !this.useCanvas && this.overlay].filter(Boolean);
      const ready = (v) =>
        v.readyState >= 4
          ? Promise.resolve()
          : new Promise((resolve) => {
              v.addEventListener('canplaythrough', resolve, { once: true });
              v.addEventListener('error', resolve, { once: true });
            });
      const capped = (promise, ms) => Promise.race([promise, new Promise((resolve) => setTimeout(resolve, ms))]);
      // Safari/iOS only buffer once playback is asked for, so they get a short cap; elsewhere up to 6s.
      // Safari draws the drone from frames, so it also waits (up to 4s) for the takeoff's frames.
      if (this.useCanvas) {
        return Promise.all([capped(Promise.all(videos.map(ready)), 1200), capped(this.takeoffFramesReady || Promise.resolve(), 4000)]);
      }
      return capped(Promise.all(videos.map(ready)), 6000);
    }

    startLoop() {
      if (this.running || !this.inView || document.hidden) return;
      this.running = true;
      this.animId = requestAnimationFrame(this.tick);
    }

    stopLoop() {
      this.running = false;
      cancelAnimationFrame(this.animId);
    }

    setInView(visible, fromTab = false) {
      if (!fromTab) this.inView = visible;
      const active = this.inView && !document.hidden;
      if (!this.sky?.getAttribute('src') || this.buffering) return;

      if (active) {
        this.startLoop();
        if (this.wasPlaying) {
          this.wasPlaying = false;
          this.start();
        }
      } else {
        this.stopLoop();
        if (this.isPlaying && !this.sky.paused) {
          this.wasPlaying = true;
          this.sky.pause();
          if (!this.useCanvas && this.overlay) this.overlay.pause();
        }
      }
    }

    destroy() {
      this.stopLoop();
      this.setSky(false);
      this.visibilityObserver?.disconnect();
      window.removeEventListener('scroll', this.onPageScroll);
      document.removeEventListener('visibilitychange', this.onTabChange);
      document.body.classList.remove('bz-in-sky');
      document.documentElement.classList.remove('bz-in-sky');
      this.root?.classList.remove('is-in-sky', 'is-flying');
      this.sky?.removeEventListener('ended', this.onSkyEnded);
      this.sky?.removeEventListener('loadedmetadata', this.onSkyMeta);
      this.sky?.removeEventListener('durationchange', this.onSkyMeta);
      this.overlay?.removeEventListener('ended', this.onEnded);
      for (const type of ['click', 'touchstart', 'keydown']) {
        window.removeEventListener(type, this.onGesture);
      }
    }

    async start() {
      try {
        if (this.skyEndedAt) {
          // Landing phase: never replay the finished sky clip from 0
          if (!this.useCanvas && this.overlay) await this.overlay.play();
        } else if (this.useCanvas) {
          await this.sky.play();
        } else {
          await Promise.all([this.sky.play(), this.overlay.play()]);
        }
        this.isPlaying = true;
        this.setState('playing');
      } catch {
        if (!this.skyEndedAt) this.sky.play().catch(() => {});
        if (!this.useCanvas && this.overlay) this.overlay.play().catch(() => {});
      }
    }

    /** Sequence clock: the sky clip's time while it plays, then carried on past its end until the landing finishes. */
    clock(now) {
      if (!this.skyEndedAt) return this.sky.currentTime;
      if (!this.useCanvas && this.overlay && !this.overlay.paused) return this.overlay.currentTime;
      const base = Number.isFinite(this.sky.duration) ? this.sky.duration : this.sky.currentTime;
      return base + Math.max(0, now - this.skyEndedAt) / 1000;
    }

    tick(now) {
      if (!this.skyEndResolved) this.onSkyMeta();

      // Some mobile browsers never fire 'ended' if the last frame is a hair short — catch it here too
      if (!this.skyEndedAt && !this.hasEnded && Number.isFinite(this.sky.duration) && this.sky.duration > 0
        && (this.sky.ended || this.sky.currentTime >= this.sky.duration - 0.02)) {
        this.skyEndedAt = now;
      }

      const skyTime = this.clock(now);
      const overlayTime = this.useCanvas ? skyTime : (this.overlay?.currentTime || 0);

      // Toggle sky mode only when crossing the transition boundaries
      // The desktop exit is timed on the overlay's own clock: the cut is hidden behind the overlay drone filling the
      // frame, and the two videos can drift up to 150ms apart
      const handoffTime = !this.useCanvas && !this.mobile && this.overlay ? overlayTime : skyTime;
      const inSky = !this.hasEnded && !this.skyEndedAt && skyTime >= this.skyStart && handoffTime <= this.skyEnd;
      if (inSky !== this.inSky) this.setSky(inSky);

      // Render transparent WebP frame on Safari / iOS Canvas
      if (this.useCanvas) {
        this.drawHeroCanvas(skyTime);
      }

      // Safety net: if the overlay stalls after the sky clip ended, still land within a beat
      const landingOverdue = this.skyEndedAt && now - this.skyEndedAt > (this.endTime - this.skyEnd) * 1000 + 1500;
      const isEnded = this.useCanvas
        ? (skyTime >= this.endTime)
        : (this.overlay?.ended || skyTime >= this.endTime || overlayTime >= this.endTime || landingOverdue
          || (this.sky.ended && !this.overlay));

      if (isEnded) {
        if (!this.hasEnded) this.finish();
      } else if (!this.useCanvas && !this.skyEndedAt && this.isPlaying && !this.sky.paused && now - this.lastSyncCheck > 350) {
        // Keep the two videos within 150ms of each other on desktop.
        // Skip on mobile to prevent hardware decoder stalls from frequent currentTime seeks.
        this.lastSyncCheck = now;
        if (!this.mobile && Math.abs(skyTime - overlayTime) > 0.15 && !this.overlay.seeking) {
          this.overlay.currentTime = skyTime;
        }
      }

      // Telemetry refresh: 90ms on desktop, throttled to 200ms on mobile to minimize DOM style ticks
      const telemetryInterval = this.mobile ? 200 : 90;
      if (now - this.lastTelemetry > telemetryInterval) {
        this.lastTelemetry = now;
        this.updateTelemetry(skyTime, now);
      }

      if (this.running) this.animId = requestAnimationFrame(this.tick);
    }

    /**
     * Fakes believable flight telemetry from the video clock:
     * climb on takeoff → cruise in the sky → descend → hover-bob once landed.
     */
    updateTelemetry(t, now) {
      const { skyStart: a, skyEnd: b, endTime: end } = this;
      const wobble = Math.sin(now / 700) * 0.6 + Math.sin(now / 1900) * 0.4;
      let alt;
      let spd;
      let mode;

      if (this.hasEnded) {
        alt = 1.2 + wobble * 0.15;
        spd = 0;
        mode = 'HOVER';
      } else if (t < a) {
        const p = t / a;
        alt = 35 * p * p;
        spd = 22 * p;
        mode = t < 0.2 ? 'STANDBY' : 'TAKEOFF';
      } else if (t <= b) {
        const p = (t - a) / (b - a);
        alt = 35 + 85 * Math.sin(Math.PI * p) + wobble * 2;
        spd = 48 + 14 * Math.sin(Math.PI * p) + wobble * 3;
        mode = 'IN FLIGHT';
      } else {
        const p = Math.min(1, (t - b) / (end - b));
        alt = Math.max(1.2, 35 * (1 - p) * (1 - p));
        spd = Math.max(0, 22 * (1 - p));
        mode = 'LANDING';
      }

      if (this.tel.alt) {
        const altStr = Math.max(0, alt).toFixed(1).padStart(5, '0');
        if (this.tel.alt.textContent !== altStr) this.tel.alt.textContent = altStr;
      }
      if (this.tel.spd) {
        const spdStr = String(Math.round(Math.max(0, spd))).padStart(2, '0');
        if (this.tel.spd.textContent !== spdStr) this.tel.spd.textContent = spdStr;
      }
      if (this.tel.bat) {
        const batStr = String(Math.round(100 - Math.min(t, end) * 0.45));
        if (this.tel.bat.textContent !== batStr) this.tel.bat.textContent = batStr;
      }
      if (this.tel.gps) {
        const gpsStr = String(18 + Math.round((wobble + 1) * 1.5));
        if (this.tel.gps.textContent !== gpsStr) this.tel.gps.textContent = gpsStr;
      }
      if (this.tel.mode && this.tel.mode.textContent !== mode) this.tel.mode.textContent = mode;
      if (this.telBar) this.telBar.style.transform = `scaleY(${Math.min(1, alt / 120)})`;

      const isFlying = mode === 'IN FLIGHT' || mode === 'TAKEOFF' || mode === 'LANDING';
      if (this.isFlying !== isFlying) {
        this.isFlying = isFlying;
        this.root.classList.toggle('is-flying', isFlying);
      }
    }

    setSky(inSky) {
      if (this.inSky === inSky) return;
      // Coming back from the sky shot: the drone lands beside the copy instead of over it (CSS moves it on desktop)
      if (this.inSky && !inSky) this.root.classList.add('has-flown');
      this.inSky = inSky;
      this.root.classList.toggle('is-in-sky', inSky);
      this.syncHeader();
    }

    /** The menu bar steps aside only while the sky shot is actually playing at the top of the page — pausing or
        scrolling brings it straight back. */
    syncHeader() {
      const hide = this.inSky && this.isPlaying && !this.hasEnded && window.scrollY < 40;
      document.body.classList.toggle('bz-in-sky', hide);
      document.documentElement.classList.toggle('bz-in-sky', hide);
    }

    /** Scrolling away during the intro: jump to the finished hero (drone landed beside the copy, Replay shown). */
    skipToEnd() {
      if (this.hasEnded || this.lite) return;
      if (!this.useCanvas && this.overlay) this.overlay.currentTime = this.endTime;
      this.finish();
    }

    setState(state) {
      if (!this.playBtn) return;
      this.playBtn.dataset.state = state;
      const labels = {
        playing: bzT('site_pause_video', 'Pause video'),
        paused: bzT('site_play_video', 'Play video'),
        ended: bzT('site_replay_video', 'Replay video from beginning'),
      };
      this.playBtn.setAttribute('aria-label', labels[state]);
    }

    // Single play-through: stop on the final landing frame and stay there
    finish() {
      this.hasEnded = true;
      this.isPlaying = false;
      this.sky.pause();
      if (!this.useCanvas && this.overlay) {
        this.overlay.pause();
        // rAF only notices endTime a frame or two late (or the clip ran to its very end) —
        // rest on exactly the endTime frame, the same final pose on every device
        if (Math.abs(this.overlay.currentTime - this.endTime) > 0.02) this.overlay.currentTime = this.endTime;
      }
      this.setSky(false);
      this.root.classList.add('is-ended', 'has-flown');
      this.root.classList.remove('is-flying');
      this.setState('ended');
      this.syncHeader();

      if (this.useCanvas) {
        this.drawHeroCanvas(this.endTime);
      }

      // On mobile, stop the continuous rAF loop once flight sequence finishes
      // to preserve 100% of CPU/GPU resources for page scrolling and user interaction
      if (this.mobile) {
        this.stopLoop();
      }

      window.dispatchEvent(new CustomEvent('bz-hero-drone-landed'));
    }

    ensureLoaded() {
      const needed = !this.sky.getAttribute('src');
      if (!this.useCanvas && this.overlay) this.loadVideo(this.overlay);
      this.loadVideo(this.sky);
      this.startLoop();
      return needed;
    }

    togglePlayPause() {
      if (this.ensureLoaded()) {
        this.restart();
        return;
      }

      const isVideoEnded = this.useCanvas
        ? (this.hasEnded || this.sky.currentTime >= this.endTime)
        : (this.hasEnded || this.overlay?.ended || this.sky.currentTime >= this.endTime);

      if (isVideoEnded) {
        this.restart();
        return;
      }

      // Once the sky clip has run out it reads as paused — only the overlay's state matters then
      const skyPaused = this.sky.paused && !this.skyEndedAt;
      const isPaused = this.useCanvas ? skyPaused : (skyPaused || this.overlay?.paused);
      if (isPaused || !this.isPlaying) {
        this.isPlaying = true;
        this.setState('playing');
        this.startLoop();
        this.start();
      } else {
        this.isPlaying = false;
        this.setState('paused');
        this.sky.pause();
        if (!this.useCanvas && this.overlay) this.overlay.pause();
      }
      this.syncHeader();
    }

    restart() {
      this.ensureLoaded();
      this.hasEnded = false;
      this.skyEndedAt = 0;
      document.body.classList.remove('bz-hero-replaced');
      window.dispatchEvent(new CustomEvent('bz-hero-drone-reset'));
      this.root.classList.remove('is-ended');
      this.root.classList.remove('is-flying');
      this.root.classList.remove('has-flown');
      this.sky.currentTime = 0;
      if (this.useCanvas) {
        this.lastHeroFrameIdx = -1;
        this.drawHeroCanvas(0);
      } else if (this.overlay) {
        this.overlay.currentTime = 0;
      }
      this.setSky(false);
      this.isPlaying = true;
      this.setState('playing');
      this.start();
    }
  }

  /** <bz-hero> — thin custom-element wrapper so the controller is shared 1:1 with the React build. */
  class BzHero extends HTMLElement {
    connectedCallback() {
      this.controller = new HeroController(this);
    }

    disconnectedCallback() {
      this.controller?.destroy();
    }
  }

  class BzCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      if (!this.track) return;

      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', this.update, { passive: true });
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);

      this.prev?.addEventListener('click', () => this.scrollByCard(-1));
      this.next?.addEventListener('click', () => this.scrollByCard(1));
      this.update();
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.update);
      this.resizeObserver?.disconnect();
    }

    scrollByCard(direction) {
      const card = this.track.querySelector('.bz-card');
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = (card?.getBoundingClientRect().width || 300) + gap;
      this.track.scrollBy({ left: direction * step, behavior: reducedMotion ? 'auto' : 'smooth' });
    }

    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      if (this.prev) this.prev.disabled = scrollLeft <= 20;
      if (this.next) this.next.disabled = scrollLeft >= scrollWidth - clientWidth - 20;
    }
  }

  class BzHeader extends HTMLElement {
    connectedCallback() {
      this.header = this.querySelector('.bz-header');
      this.burger = this.querySelector('[data-burger]');
      this.progress = this.querySelector('[data-progress]');
      if (!this.header) return;

      // Glass + compact state once scrolled, and a scroll-progress line
      let ticking = false;
      this.onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const { y, max } = scrollState();
          this.header.classList.toggle('is-scrolled', y > 24);
          if (this.progress) {
            this.progress.style.transform = `scaleX(${max > 0 ? Math.min(1, y / max) : 0})`;
          }
        });
      };
      // Capture phase catches scrolls of .page-wrapper as well as the window
      document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.onScroll();

      // Mega menu (desktop): Drones / Accessories / Categories open a floating panel on hover, or on the first
      // click/tap when it isn't open yet (a second click follows the link to the collection)
      this.megaLinks = [...this.querySelectorAll('[data-mega]')];
      this.megaPanels = new Map([...this.querySelectorAll('[data-mega-panel]')].map((el) => [el.dataset.megaPanel, el]));
      if (this.megaLinks.length && this.megaPanels.size) {
        const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        let openTimer = 0;
        let closeTimer = 0;
        this.openMega = (key) => {
          clearTimeout(closeTimer);
          if (this.megaOpen === key) return;
          for (const [k, panel] of this.megaPanels) panel.hidden = k !== key;
          for (const link of this.megaLinks) link.setAttribute('aria-expanded', String(link.dataset.mega === key));
          this.megaOpen = key;
          this.header.classList.toggle('is-mega-open', !!key);
        };
        this.closeMega = () => {
          clearTimeout(openTimer);
          if (!this.megaOpen) return;
          for (const panel of this.megaPanels.values()) panel.hidden = true;
          for (const link of this.megaLinks) link.setAttribute('aria-expanded', 'false');
          this.megaOpen = null;
          this.header.classList.remove('is-mega-open');
        };
        const scheduleClose = () => {
          clearTimeout(openTimer);
          clearTimeout(closeTimer);
          closeTimer = setTimeout(this.closeMega, 220);
        };
        for (const link of this.megaLinks) {
          if (canHover) {
            link.addEventListener('mouseenter', () => {
              clearTimeout(closeTimer);
              clearTimeout(openTimer);
              openTimer = setTimeout(() => this.openMega(link.dataset.mega), this.megaOpen ? 0 : 90);
            });
            link.addEventListener('mouseleave', scheduleClose);
          }
          link.addEventListener('click', (event) => {
            if (this.megaOpen === link.dataset.mega) return;
            event.preventDefault();
            this.openMega(link.dataset.mega);
          });
        }
        for (const panel of this.megaPanels.values()) {
          panel.addEventListener('mouseenter', () => clearTimeout(closeTimer));
          panel.addEventListener('mouseleave', scheduleClose);
        }
        document.addEventListener('click', (event) => {
          if (this.megaOpen && !(event.target instanceof Element && event.target.closest('[data-mega], [data-mega-panel]'))) this.closeMega();
        });
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.megaOpen) {
            const link = this.megaLinks.find((l) => l.dataset.mega === this.megaOpen);
            this.closeMega();
            link?.focus();
          }
        });
      }

      // Search icon / menu link: open the instant search popup in place; the link's search page is only the fallback
      this.addEventListener('click', (event) => {
        const link = event.target instanceof Element ? event.target.closest('[data-open-search]') : null;
        if (!link || !window.bzSearch) return;
        event.preventDefault();
        if (this.header.classList.contains('is-open')) this.toggle(false);
        this.closeMega?.();
        window.bzSearch.open();
      });

      if (!this.burger) return;

      this.burger.addEventListener('click', () => this.toggle());
      this.onKey = (event) => {
        if (event.key === 'Escape' && this.header.classList.contains('is-open')) {
          this.toggle(false);
          this.burger.focus();
        }
      };
      document.addEventListener('keydown', this.onKey);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('scroll', this.onScroll, { capture: true });
      window.removeEventListener('resize', this.onScroll);
    }

    toggle(force) {
      const open = this.header.classList.toggle('is-open', force);
      this.burger.setAttribute('aria-expanded', String(open));
      document.documentElement.classList.toggle('bz-menu-open', open);
    }
  }


  /* ---------- Traveling drone: hero → featured product card (shared with React DroneFlight.tsx) ---------- */
  // Exact tight bounds of the drone in the 1920×1080 hero frame (drone-cutout.webp is 910×435).
  const FLIGHT_BASE_W = 910;
  const FLIGHT_BASE_CX = 455;
  const FLIGHT_BASE_CY = 217.5;
  const FLIGHT_DAMPING = 24;

  /** The hero drone element actually on screen: the alpha video, or the canvas used on Safari/iOS. */
  const visibleHeroDrone = () =>
    Array.from(document.querySelectorAll('.bz-hero__overlay')).find((el) => el.offsetWidth > 0 && el.offsetHeight > 0) || null;

  /** Translation the hero's CSS hover-bob animation currently applies to an element. */
  const animatedOffset = (el) => {
    if (!el) return { x: 0, y: 0 };
    const t = getComputedStyle(el).transform;
    if (!t || t === 'none') return { x: 0, y: 0 };
    const m = new DOMMatrixReadOnly(t);
    return { x: m.m41, y: m.m42 };
  };

  class BzDroneFlight extends HTMLElement {
    connectedCallback() {
      this.container = this.querySelector('.bz-drone-flight');
      if (!this.container) return;
      this.dockHandle = this.dataset.dockHandle || 'dji-lito-x1';
      this.isReplaced = false;
      this.targetT = 0;
      this.currentT = 0;
      this.running = false;
      this.lastTime = 0;
      this.docked = false;
      this.geo = null;
      this.hasLanded = !!document.querySelector('.bz-hero.is-ended');
      // Where the hover-bob had the hero drone at the moment of handoff; fades out over the flight
      this.handoffBob = { x: 0, y: 0 };

      this.render = this.render.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.updateGeometry = this.updateGeometry.bind(this);
      this.onLanded = () => {
        this.hasLanded = true;
        this.updateGeometry();
        // Already scrolled past the flight path: dock straight away instead of blanking the card and flying in
        if (this.geo && window.scrollY >= this.geo.endScroll) this.currentT = this.targetT = 1;
        this.onScroll();
      };
      this.onReset = () => {
        this.hasLanded = false;
        this.targetCard?.classList.remove('is-drone-docked');
        this.docked = false;
        this.setReplaced(false);
      };
      this.onResize = () => {
        cancelAnimationFrame(this.resizeRaf);
        this.resizeRaf = requestAnimationFrame(this.updateGeometry);
      };

      this.updateGeometry();
      this.timer = setTimeout(this.updateGeometry, 350);
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('load', this.onResize, { once: true });
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('bz-hero-drone-landed', this.onLanded);
      window.addEventListener('bz-hero-drone-reset', this.onReset);
      this.track = document.querySelector('#bz-featured .bz-products__track');
      this.track?.addEventListener('scroll', this.updateGeometry, { passive: true });
      // The landed drone glides to its centred spot (has-flown); re-measure when it gets there, so a flight that starts
      // right after a skipped intro begins where the drone actually is
      this.onOverlayMoved = (event) => {
        if (event.target instanceof Element && event.target.matches('.bz-hero__overlay')) this.updateGeometry();
      };
      this.hero = document.querySelector('.bz-hero');
      this.hero?.addEventListener('transitionend', this.onOverlayMoved);
      this.onScroll();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      cancelAnimationFrame(this.resizeRaf);
      cancelAnimationFrame(this.animId);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('bz-hero-drone-landed', this.onLanded);
      window.removeEventListener('bz-hero-drone-reset', this.onReset);
      this.track?.removeEventListener('scroll', this.updateGeometry);
      this.hero?.removeEventListener('transitionend', this.onOverlayMoved);
      document.body.classList.remove('bz-hero-replaced');
    }

    updateGeometry() {
      // Measure whichever drone layer is visible — on iOS the <video> is display:none and the canvas draws the
      // drone, so measuring the video gave a 0x0 box at the top-left corner
      const heroVideo = visibleHeroDrone();
      const hero = document.querySelector('.bz-hero');
      this.targetCard = document.querySelector(`#bz-featured [data-product-handle="${this.dockHandle}"]`);
      const media = this.targetCard?.querySelector('[data-card-media]');
      if (!heroVideo || !hero || !media) {
        this.geo = null;
        return;
      }

      const heroRect = heroVideo.getBoundingClientRect();
      const sy = window.scrollY;
      const sx = window.scrollX;
      // Flight coordinates are relative to the portal, and must not include the hover-bob's momentary offset
      // (it is added back at handoff instead)
      const portalRect = this.container.offsetParent?.getBoundingClientRect();
      const ox = portalRect ? portalRect.left + sx : 0;
      const oy = portalRect ? portalRect.top + sy : 0;
      const bob = animatedOffset(heroVideo);
      const cw = heroRect.width;
      const ch = heroRect.height;
      const videoAspect = 16 / 9;
      let vw;
      let vh;
      let vx;
      let vy;
      // Mirrors object-fit: cover; object-position: 60% center (desktop) / center (mobile)
      if (cw / Math.max(1, ch) >= videoAspect) {
        vw = cw;
        vh = cw / videoAspect;
        vx = heroRect.left + sx - ox - bob.x;
        vy = heroRect.top + sy - oy - bob.y + (ch - vh) * 0.5;
      } else {
        vh = ch;
        vw = ch * videoAspect;
        vy = heroRect.top + sy - oy - bob.y;
        vx = heroRect.left + sx - ox - bob.x + (cw - vw) * (window.innerWidth >= 900 ? 0.6 : 0.5);
      }

      // Where the landed drone sits in the 1920×1080 overlay frame at endTime, fitted against drone-cutout.webp:
      // centre (961, 524), width 764px. The old values (910px wide, y 0.4995) made the flying drone ~18% bigger and
      // lower than the landed one, so it jumped the moment the flight took over.
      const p0 = { x: vx + vw * 0.5005208, y: vy + vh * 0.4851852 };
      const m = media.getBoundingClientRect();
      const p3 = { x: m.left + sx - ox + m.width * 0.5, y: m.top + sy - oy + m.height * 0.5 };
      const dy = p3.y - p0.y;
      // Staged downward flight: leaves the hero straight down, then curves into the card
      const p1 = { x: p0.x, y: p0.y + dy * 0.42 };
      const p2 = { x: p3.x, y: p3.y - dy * 0.32 };
      // Phones draw the hero drone much smaller than desktop: boost it mid-flight. The boost rides the flight's
      // envelope, so the drone starts exactly the hero's size and lands exactly the card's size.
      const boost = window.innerWidth < 900 ? 1.35 : 1;
      const startScale = (vw * 0.3979167) / FLIGHT_BASE_W;
      const endScale = m.width / FLIGHT_BASE_W;
      const endScroll = Math.max(100, p3.y + oy - document.documentElement.clientHeight * 0.45);
      this.geo = { p0, p1, p2, p3, startScale, endScale, boost, startScroll: 2, endScroll };

      if (sy <= 2) {
        this.container.style.transform = `translate3d(${p0.x - FLIGHT_BASE_CX}px, ${p0.y - FLIGHT_BASE_CY}px, 0) scale(${startScale})`;
      }
    }

    setReplaced(on) {
      // Pick the drone up exactly where the hover-bob has it right now
      if (on && !this.isReplaced) this.handoffBob = animatedOffset(visibleHeroDrone());
      this.isReplaced = on;
      document.body.classList.toggle('bz-hero-replaced', on);
      this.container.style.opacity = on ? '1' : '0';
      if (!on) {
        this.currentT = 0;
        this.targetT = 0;
        const g = this.geo;
        if (g) this.container.style.transform = `translate3d(${g.p0.x - FLIGHT_BASE_CX}px, ${g.p0.y - FLIGHT_BASE_CY}px, 0) scale(${g.startScale})`;
      }
    }

    render(now) {
      const g = this.geo;
      if (!g) {
        this.running = false;
        return;
      }
      if (!this.lastTime) this.lastTime = now;
      const dt = Math.min(0.064, Math.max(0.001, (now - this.lastTime) / 1000));
      this.lastTime = now;
      const diff = this.targetT - this.currentT;
      if (Math.abs(diff) < 0.0004) this.currentT = this.targetT;
      else this.currentT += diff * (1 - Math.exp(-FLIGHT_DAMPING * dt));

      const t = this.currentT;
      const mt = 1 - t;
      const mt2 = mt * mt;
      const t2 = t * t;
      const x = mt2 * mt * g.p0.x + 3 * mt2 * t * g.p1.x + 3 * mt * t2 * g.p2.x + t2 * t * g.p3.x;
      const y = mt2 * mt * g.p0.y + 3 * mt2 * t * g.p1.y + 3 * mt * t2 * g.p2.y + t2 * t * g.p3.y;
      const tanx = 3 * mt2 * (g.p1.x - g.p0.x) + 6 * mt * t * (g.p2.x - g.p1.x) + 3 * t2 * (g.p3.x - g.p2.x);
      const tany = 3 * mt2 * (g.p1.y - g.p0.y) + 6 * mt * t * (g.p2.y - g.p1.y) + 3 * t2 * (g.p3.y - g.p2.y);
      const envelope = Math.sin(t * Math.PI);
      const scale = (g.startScale + (g.endScale - g.startScale) * t) * (1 + (g.boost - 1) * envelope);
      const trajectoryBank = Math.max(-10, Math.min(10, Math.atan2(tanx, Math.max(1, tany)) * (180 / Math.PI) * 0.7));
      const airRoll = Math.sin(t * Math.PI * 3.5) * 3.5;
      const velocityTilt = Math.max(-4, Math.min(4, (this.targetT - this.currentT) * 30));
      const bank = (trajectoryBank + airRoll + velocityTilt) * envelope;
      const bx = this.handoffBob.x * mt;
      const by = this.handoffBob.y * mt;
      this.container.style.transform = `translate3d(${x - FLIGHT_BASE_CX + bx}px, ${y - FLIGHT_BASE_CY + by}px, 0) scale(${scale}) rotate(${bank}deg)`;

      const dock = t >= 0.995;
      if (dock !== this.docked && this.targetCard) {
        this.docked = dock;
        this.targetCard.classList.toggle('is-drone-docked', dock);
        this.container.style.opacity = dock ? '0' : this.isReplaced ? '1' : '0';
      }

      if (Math.abs(this.targetT - this.currentT) > 0.0004) this.animId = requestAnimationFrame(this.render);
      else {
        this.running = false;
        this.lastTime = 0;
      }
    }

    onScroll() {
      // The drone only travels once the hero flight has landed
      if (!this.hasLanded || !this.geo) {
        if (this.isReplaced) this.setReplaced(false);
        return;
      }
      const sy = window.scrollY;
      const g = this.geo;
      if (sy > 2) {
        if (!this.isReplaced) this.setReplaced(true);
      } else if (this.isReplaced) this.setReplaced(false);

      this.targetT = sy <= g.startScroll ? 0 : sy >= g.endScroll ? 1 : (sy - g.startScroll) / (g.endScroll - g.startScroll);
      if (!this.running) {
        this.running = true;
        this.lastTime = 0;
        this.animId = requestAnimationFrame(this.render);
      }
    }
  }

  /* ---------- Cinematic Dominance: scroll-scrubbed 72-frame drone (shared with React CinematicDominance.tsx) ---------- */
  const lowEndDevice = (() => {
    const cores = navigator.hardwareConcurrency || 4;
    const memory = navigator.deviceMemory || 8;
    const conn = navigator.connection || {};
    return reducedMotion || cores <= 4 || memory <= 3 || conn.saveData === true || /(^|-)2g$/.test(conn.effectiveType || '');
  })();
  if (lowEndDevice) document.documentElement.classList.add('bz-low-end');

  // Per-frame nudge (x, y pairs in 1280x720 frame pixels) that eases the clip's own camera motion: the drone hops
  // ~34px up over frames 6-11 and stops dead, which read as a jolt as it starts coming apart. Measured by phase
  // correlation between neighbouring frames, then smoothed — the drift stays, the jolt goes (≤3px per frame).
  const CINE_NUDGE = [1, -1, 1, -2, 1, -3, 2, -4, 2, -6, 2, -9, 2, -9, 2, -9, 0, -3, -1, 1, -1, 4, -4, 13, -2, 9, -1, 8, -2, 6, -1, 5, -1, 4, 0, 2, 0, 1, 0, 1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, -1, -1, -1, -1, -2, -1, -3, -2, -4, -3, -5, -3, -3, -2, -3, -2, -2, -1, 0, 0, -1, 0, 0, 1, 1, 2, 0, 2, 1, 2, 1, 3, 0, 2, 0, 2, 1, 1, 1, 1, 0, 1, 1, 0, 1, 0, 0, 0, 1, 0, 1, 0, 1, 0, 2, 0, 3, 0];

  class BzCinematic extends HTMLElement {
    connectedCallback() {
      this.canvas = this.querySelector('canvas');
      this.fill = this.querySelector('.bz-cinematic__scrub-fill');
      this.poster = this.querySelector('.bz-cinematic__img');
      this.total = parseInt(this.dataset.frames, 10) || 72;
      this.frames = new Array(this.total);
      this.target = 0;
      this.current = 0;
      this.lastDrawn = -1;
      this.animating = false;
      this.lastTime = 0;
      this.loaded = 0;
      this.started = false;
      this.dpr = lowEndDevice ? 1 : Math.min(2, window.devicePixelRatio || 1);
      if (!this.canvas) return;
      this.ctx = this.canvas.getContext('2d', { alpha: true });

      this.loop = this.loop.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.measure = this.measure.bind(this);
      this.onResize = () => {
        cancelAnimationFrame(this.resizeRaf);
        this.resizeRaf = requestAnimationFrame(this.measure);
      };

      // Start downloading frames only when the section is within ~1.5 screens
      this.io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this.io.disconnect();
            this.load();
          }
        },
        { rootMargin: '150% 0px' }
      );
      this.io.observe(this);

      this.measure();
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('load', this.onResize, { once: true });
      // Keep the cached position right when content above changes height
      this.ro = 'ResizeObserver' in window ? new ResizeObserver(this.onResize) : null;
      this.ro?.observe(document.body);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      this.io?.disconnect();
      this.ro?.disconnect();
      cancelAnimationFrame(this.resizeRaf);
      cancelAnimationFrame(this.animId);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      this.cancelled = true;
    }

    frameUrl(n) {
      return (this.dataset.frameUrl || '').replace('cine-frame-001', `cine-frame-${String(n).padStart(3, '0')}`);
    }

    load() {
      if (this.started) return;
      this.started = true;
      const threshold = lowEndDevice ? 24 : 46;
      for (let i = 0; i < this.total; i++) {
        const img = new Image();
        img.src = this.frameUrl(i + 1);
        const done = () => {
          if (this.cancelled) return;
          this.frames[i] = img;
          if (i === 0) this.draw(0, true);
          this.loaded++;
          if (this.loaded >= threshold && this.poster) this.poster.style.opacity = '0';
        };
        (img.decode ? img.decode() : Promise.reject()).then(done, () => (img.complete ? done() : (img.onload = done)));
      }
    }

    measure() {
      const rect = this.getBoundingClientRect();
      this.pinTop = rect.top + window.scrollY;
      // the sticky stage is 100svh, so measure against the small viewport (clientHeight), which stays put while a
      // phone's address bar slides away — innerHeight changes then and made the disassembly jump mid-scroll
      this.distance = Math.max(1, this.offsetHeight - document.documentElement.clientHeight);
      const w = Math.round(1280 * this.dpr);
      const h = Math.round(720 * this.dpr);
      if (this.canvas.width !== w || this.canvas.height !== h) {
        this.canvas.width = w;
        this.canvas.height = h;
        this.lastDrawn = -1;
        this.draw(this.frameFor(this.current));
      }
      this.onScroll();
    }

    // Hold the assembled drone for the first 12% of the scroll and the exploded view for the last 12%,
    // with an eased ramp between so the parts separate gradually instead of all at once
    frameFor(p) {
      const t = Math.max(0, Math.min(1, (p - 0.12) / 0.76));
      return Math.min(this.total - 1, Math.max(0, Math.round(t * t * (3 - 2 * t) * (this.total - 1))));
    }

    draw(i, force) {
      if (!force && i === this.lastDrawn) return;
      const img = this.frames[i];
      if (!img || !img.complete || !img.naturalWidth) return;
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.ctx.imageSmoothingEnabled = true;
      this.ctx.imageSmoothingQuality = lowEndDevice ? 'medium' : 'high';
      const k = this.canvas.width / 1280;
      this.ctx.drawImage(img, (CINE_NUDGE[i * 2] || 0) * k, (CINE_NUDGE[i * 2 + 1] || 0) * k, this.canvas.width, this.canvas.height);
      this.lastDrawn = i;
    }

    onScroll() {
      if (this.pinTop == null) return;
      this.target = Math.max(0, Math.min(1, (window.scrollY - this.pinTop) / this.distance));
      if (!this.animating) {
        this.animating = true;
        this.lastTime = 0;
        this.animId = requestAnimationFrame(this.loop);
      }
    }

    loop(time) {
      const dt = this.lastTime ? Math.min(0.064, Math.max(0.001, (time - this.lastTime) / 1000)) : 0.016;
      this.lastTime = time;
      const diff = this.target - this.current;
      if (Math.abs(diff) < 0.0008) this.current = this.target;
      // soft damping: a quick flick plays the disassembly through instead of jumping to the end
      else this.current += diff * (1 - Math.exp(-9 * dt));
      const p = Math.max(0, Math.min(1, this.current));
      this.draw(this.frameFor(p));
      if (this.fill) this.fill.style.transform = `scaleX(${p})`;
      if (Math.abs(this.target - this.current) > 0.0008) this.animId = requestAnimationFrame(this.loop);
      else {
        this.animating = false;
        this.lastTime = 0;
      }
    }
  }

  /* ---------- About hero: globe plays once, then the copy reveals (no scroll hijacking) ---------- */
  class BzAboutHero extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      // 1920px globe clip on desktop, 1280px on phones (no src in the markup, so only one is ever fetched)
      if (this.video && !this.video.getAttribute('src')) {
        const d = this.video.dataset;
        const src = window.matchMedia('(max-width: 899px)').matches ? d.srcMobile || d.srcDesktop : d.srcDesktop;
        if (src) this.video.src = src;
      }
      this.fill = this.querySelector('.bz-about-hero__scroll-fill');
      this.cue = this.querySelector('.bz-about-hero__scroll-cue');
      const section = this.querySelector('.bz-about-hero') || this;
      const reveal = () => {
        if (this.revealed) return;
        this.revealed = true;
        cancelAnimationFrame(this.raf);
        clearTimeout(this.safety);
        if (this.fill) this.fill.style.transform = 'scaleX(1)';
        section.classList.add('is-revealed');
        this.cue?.classList.add('is-hidden');
      };
      this.reveal = reveal;
      if (!this.video || reducedMotion) {
        reveal();
        return;
      }
      const track = () => {
        if (this.revealed) return;
        const v = this.video;
        if (v.duration > 0 && this.fill) this.fill.style.transform = `scaleX(${Math.max(0.04, Math.min(1, v.currentTime / v.duration))})`;
        if (v.duration > 0 && v.currentTime >= v.duration - 0.08) {
          v.pause();
          reveal();
          return;
        }
        this.raf = requestAnimationFrame(track);
      };
      this.safety = setTimeout(reveal, 7000);
      this.video.addEventListener('ended', reveal, { once: true });
      this.video.addEventListener('error', reveal, { once: true });
      this.video.muted = true;
      // Played a bit faster so the intro doesn't keep visitors waiting
      this.video.playbackRate = parseFloat(this.dataset.rate) || 1.6;
      const play = this.video.play();
      if (play && play.then) play.then(() => (this.raf = requestAnimationFrame(track)), reveal);
      section.addEventListener('click', reveal, { once: true });
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      clearTimeout(this.safety);
    }
  }

  customElements.define('bz-hero', BzHero);
  customElements.define('bz-drone-flight', BzDroneFlight);
  customElements.define('bz-cinematic', BzCinematic);
  customElements.define('bz-about-hero', BzAboutHero);
  customElements.define('bz-carousel', BzCarousel);
  customElements.define('bz-header', BzHeader);

  // Scroll reveal
  const revealObserver =
    'IntersectionObserver' in window && !reducedMotion
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              entry.target.classList.add('is-visible');
              revealObserver.unobserve(entry.target);
            }
          },
          { rootMargin: '0px 0px -10% 0px' }
        )
      : null;

  const observeReveals = (root = document) => {
    for (const el of root.querySelectorAll('.bz-reveal:not(.is-visible)')) {
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('is-visible');
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => observeReveals());
  } else {
    observeReveals();
  }

  // Pause looping CSS/SVG animations in sections that are off screen (saves battery + main thread)
  const animatedSelector = '.bz-brands, .bz-cta, .bz-page-hero, .bz-lost, .bz-footer, .bz-network, .bz-cinematic, .bz-cat-hero, .bz-faq-hero, .bz-about-hero';
  const animObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              const off = !entry.isIntersecting;
              entry.target.classList.toggle('bz-offscreen', off);
              for (const svg of entry.target.querySelectorAll('svg')) {
                if (typeof svg.pauseAnimations !== 'function') continue;
                if (off) svg.pauseAnimations();
                else svg.unpauseAnimations();
              }
            }
          },
          { rootMargin: '100px 0px' }
        )
      : null;
  const observeAnimated = (root = document) => {
    if (!animObserver) return;
    for (const el of root.querySelectorAll(animatedSelector)) animObserver.observe(el);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => observeAnimated());
  else observeAnimated();
  document.addEventListener('shopify:section:load', (event) => observeAnimated(event.target));

  // Cursor spotlight: cards expose --mx/--my so CSS can paint a glow under the pointer
  if (!reducedMotion && window.matchMedia('(hover: hover)').matches) {
    const spotSelector = '.bz-card, .bz-feature, .bz-cat, .bz-stat, .bz-faq__item, .bz-brand';
    let spotRaf = 0;
    let spotEvent = null;
    document.addEventListener(
      'pointermove',
      (event) => {
        spotEvent = event;
        if (spotRaf) return;
        spotRaf = requestAnimationFrame(() => {
          spotRaf = 0;
          const e = spotEvent;
          const card = e.target instanceof Element ? e.target.closest(spotSelector) : null;
          if (!card) return;
          const rect = card.getBoundingClientRect();
          card.style.setProperty('--mx', `${e.clientX - rect.left}px`);
          card.style.setProperty('--my', `${e.clientY - rect.top}px`);
        });
      },
      { passive: true }
    );
  }

  // Smart search (snippets/bugiz-search.liquid): instant, typo-tolerant suggestions from a small product/category index
  class BzSearch {
    constructor(dialog) {
      this.dialog = dialog;
      this.input = dialog.querySelector('[data-bz-search-input]');
      this.form = dialog.querySelector('[data-bz-search-form]');
      this.start = dialog.querySelector('[data-bz-search-start]');
      this.list = dialog.querySelector('[data-bz-search-results]');
      this.d = dialog.dataset;
      this.items = [];
      this.active = -1;
      this.index = null;
      this.input.addEventListener('input', () => this.render());
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog || (e.target instanceof Element && e.target.closest('[data-close]'))) dialog.close();
        const chip = e.target instanceof Element ? e.target.closest('[data-bz-search-term]') : null;
        if (chip) {
          this.input.value = chip.dataset.bzSearchTerm;
          this.render();
          this.input.focus();
        }
      });
    }

    open() {
      if (!this.dialog.open) this.dialog.showModal();
      this.input.focus();
      this.input.select();
      this.load();
    }

    async load() {
      if (this.index || this.loading) return;
      const url = this.d.indexUrl;
      try {
        const cached = sessionStorage.getItem('bz-search:' + url);
        if (cached) this.index = this.prepare(JSON.parse(cached));
      } catch {}
      if (this.index) return this.render();
      this.loading = true;
      try {
        const res = await fetch(url, { credentials: 'same-origin' });
        const data = await res.json();
        this.index = this.prepare(data);
        try {
          sessionStorage.setItem('bz-search:' + url, JSON.stringify(data));
        } catch {}
      } catch {
        this.index = { products: [], collections: [] };
      }
      this.loading = false;
      this.render();
    }

    static norm(text) {
      return String(text || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    /** Singular stem so "batteries" finds "Battery" and "baterías" finds "batería" */
    static stem(w) {
      if (w.length > 4 && w.endsWith('ies')) return w.slice(0, -3) + 'y';
      if (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) return w.slice(0, -1);
      return w;
    }

    prepare(data) {
      const words = (...parts) => [...new Set(parts.map(BzSearch.norm).join(' ').split(' ').filter(Boolean).map(BzSearch.stem))];
      return {
        products: (data.products || []).map((p) => {
          const flat = BzSearch.norm(p.t);
          const brand = BzSearch.norm(p.v).split(' ')[0];
          return { ...p, title: words(p.t), type: words(p.y), extra: words(p.v, p.k), flat, lead: brand && flat.startsWith(brand + ' ') ? flat.slice(brand.length + 1) : flat };
        }),
        collections: (data.collections || []).map((c) => ({ ...c, title: words(c.t), type: [], extra: [], flat: BzSearch.norm(c.t), lead: BzSearch.norm(c.t) })),
      };
    }

    // Damerau–Levenshtein distance (optimal string alignment), capped for speed
    static dist(a, b) {
      if (Math.abs(a.length - b.length) > 2) return 9;
      const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
      for (let j = 1; j <= b.length; j++) d[0][j] = j;
      for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
          const cost = a[i - 1] === b[j - 1] ? 0 : 1;
          d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
          if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
        }
      }
      return d[a.length][b.length];
    }

    /** How well query word q matches word w: 4 exact, 3 prefix, 2 inside, 1 typo, 0 none (+ the matched word) */
    static wordScore(q, w) {
      if (w === q) return 4;
      if (w.startsWith(q)) return 3;
      if (q.length >= 3 && w.includes(q)) return 2;
      if (q.length >= 3 && !/^\d+$/.test(q)) {
        const allowed = q.length >= 7 ? 2 : 1;
        if (BzSearch.dist(q, w) <= allowed) return 1;
        // a typo while still typing a longer word ("avat" → "avata-…") — weaker than a whole-word typo
        if (q.length >= 4 && w.length > q.length && BzSearch.dist(q, w.slice(0, q.length)) <= 1) return 0.5;
      }
      return 0;
    }

    match(entry, terms) {
      let score = 0;
      const fixes = [];
      for (const q of terms) {
        let best = 0;
        let bestWord = '';
        for (const w of entry.title) {
          const s = BzSearch.wordScore(q, w) * 2;
          if (s > best) [best, bestWord] = [s, w];
        }
        let typeHit = false;
        const head = entry.type[entry.type.length - 1];
        for (const w of entry.type) {
          const s = BzSearch.wordScore(q, w) * 2;
          // the type's main noun only: "Goggles Battery" is a battery, not goggles
          if (s >= 2 && w === head) typeHit = true;
          if (s > best) [best, bestWord] = [s, w];
        }
        for (const w of entry.extra) {
          const s = BzSearch.wordScore(q, w);
          if (s > best) [best, bestWord] = [s, w];
        }
        if (!best) return null;
        // what the product *is* ("battery" → batteries before drone bundles that include one)
        score += best + (typeHit ? 3 : 0);
        fixes.push(best <= 2 && bestWord ? bestWord : q);
      }
      // the words in the order typed ("rc 2" → "DJI RC 2 …" before "DJI RC Plus 2"), best of all at the start of the name
      const phrase = terms.join(' ');
      if (terms.length > 1 && entry.flat.includes(phrase)) score += 6;
      if (entry.lead.startsWith(phrase)) score += terms.length > 1 ? 4 : 2;
      return { entry, score, fixes };
    }

    search(query) {
      const raw = BzSearch.norm(query).split(' ').filter(Boolean);
      const terms = raw.map(BzSearch.stem);
      if (!terms.length || !this.index) return { products: [], collections: [], fixed: '' };
      const rank = (list) =>
        list
          .map((e) => this.match(e, terms))
          .filter(Boolean)
          .sort((x, y) => y.score - x.score || (y.entry.d || 0) - (x.entry.d || 0) || x.entry.t.length - y.entry.t.length);
      const products = rank(this.index.products);
      const collections = rank(this.index.collections);
      // a typo correction for "View all"/Enter: only when nothing matches literally
      const literal = this.index.products.some((p) => raw.every((q) => p.flat.includes(q) || p.extra.some((w) => w.startsWith(BzSearch.stem(q)))));
      const fixed = !literal && products.length ? products[0].fixes.join(' ') : '';
      return { products: products.slice(0, 6).map((r) => r.entry), collections: collections.slice(0, 3).map((r) => r.entry), fixed };
    }

    render() {
      const q = this.input.value.trim();
      this.active = -1;
      this.input.removeAttribute('aria-activedescendant');
      if (!q) {
        this.start.hidden = false;
        this.list.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
        return;
      }
      this.start.hidden = true;
      this.list.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      if (!this.index) {
        this.list.innerHTML = '<p class="bz-search__note">…</p>';
        return;
      }
      const { products, collections, fixed } = this.search(q);
      this.fixed = fixed;
      const esc = (t) => String(t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
      const img = (src) => (src ? `<img src="${esc(src)}" alt="" width="56" height="56">` : '');
      let html = '';
      let n = 0;
      if (fixed) html += `<p class="bz-search__fixed">${esc(this.d.labelFixed)} <strong>${esc(fixed)}</strong></p>`;
      if (products.length) {
        html += `<p class="bz-search__heading">${esc(this.d.labelProducts)}</p>`;
        for (const p of products) {
          html += `<a class="bz-search__row" role="option" id="bz-opt-${n++}" href="${esc(p.u)}"><span class="bz-search__thumb">${img(p.i)}</span><span class="bz-search__txt"><span class="bz-search__name">${esc(p.t)}</span><span class="bz-search__meta">${esc(p.v)}${p.y ? ' · ' + esc(p.y) : ''}</span></span><span class="bz-search__price">${esc(p.p)}</span></a>`;
        }
      }
      if (collections.length) {
        html += `<p class="bz-search__heading">${esc(this.d.labelCategories)}</p>`;
        for (const c of collections) {
          html += `<a class="bz-search__row bz-search__row--cat" role="option" id="bz-opt-${n++}" href="${esc(c.u)}"><span class="bz-search__thumb">${img(c.i)}</span><span class="bz-search__txt"><span class="bz-search__name">${esc(c.t)}</span></span><span class="bz-search__count">${c.n}</span></a>`;
        }
      }
      if (!products.length && !collections.length) html += `<p class="bz-search__note">${esc(this.d.labelNone)}</p>`;
      html += `<a class="bz-search__all" href="${esc(this.resultsUrl(fixed || q))}">${esc(this.d.labelAll)} “${esc(fixed || q)}” <span aria-hidden="true">→</span></a>`;
      this.list.innerHTML = html;
      this.items = [...this.list.querySelectorAll('[role="option"]')];
    }

    resultsUrl(q) {
      return `${this.d.searchUrl}?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last`;
    }

    onKey(e) {
      if (!this.items.length || this.list.hidden) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const step = e.key === 'ArrowDown' ? 1 : -1;
        // cycles through the options and back to the input (-1)
        this.active += step;
        if (this.active >= this.items.length) this.active = -1;
        if (this.active < -1) this.active = this.items.length - 1;
        this.items.forEach((el, i) => el.classList.toggle('is-active', i === this.active));
        const el = this.items[this.active];
        if (el) {
          this.input.setAttribute('aria-activedescendant', el.id);
          el.scrollIntoView({ block: 'nearest' });
        } else this.input.removeAttribute('aria-activedescendant');
      } else if (e.key === 'Enter' && this.items[this.active]) {
        e.preventDefault();
        window.location.href = this.items[this.active].href;
      }
    }

    onSubmit(e) {
      const q = this.input.value.trim();
      if (!q) {
        e.preventDefault();
        return;
      }
      if (this.fixed) {
        e.preventDefault();
        window.location.href = this.resultsUrl(this.fixed);
      }
    }
  }

  const searchDialog = document.querySelector('[data-bz-search]');
  if (searchDialog) window.bzSearch = new BzSearch(searchDialog);

  // Waiting list: people who tick "Keep me updated" are also saved as a Shopify customer (Customers, tagged
  // "waiting list" + product, WhatsApp and phone) through the storefront customer form, which subscribes them to email
  // marketing, so only with that consent. The contact form still emails every signup.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.bzWlSent) return;
    const request = form.querySelector('input[name="contact[Request]"]');
    const offers = form.querySelector('[data-bz-offers]');
    if (!request || !request.value.startsWith('Waiting list') || !offers || offers.disabled || !offers.checked) return;
    event.preventDefault();
    const val = (name) => (form.querySelector(`[name="contact[${name}]"]`)?.value || '').trim();
    const tag = (text) => text.replace(/,/g, ' ').slice(0, 200);
    const [first = '', ...rest] = val('name').split(/\s+/);
    const tags = ['waiting list', `waitlist: ${tag(val('Product'))}`, `whatsapp: ${tag(val('WhatsApp'))}`];
    if (val('phone')) tags.push(`phone: ${tag(val('phone'))}`);
    const body = new URLSearchParams({
      form_type: 'customer',
      utf8: '✓',
      'contact[email]': val('email'),
      'contact[first_name]': first,
      'contact[last_name]': rest.join(' '),
      'contact[tags]': tags.join(','),
    });
    let sent = false;
    const send = () => {
      if (sent) return;
      sent = true;
      form.dataset.bzWlSent = '1';
      form.submit();
    };
    fetch(`${window.Shopify?.routes?.root || '/'}contact`, { method: 'POST', body, credentials: 'same-origin' })
      .catch(() => {})
      .finally(send);
    setTimeout(send, 4000); // never hold up the signup email
  }, true);

  // Product cards: "Request a Quote" opens the shared lead form (snippets/bugiz-quote-dialog) for that product
  const cardQuote = document.querySelector('[data-bz-card-quote]');
  if (cardQuote) {
    const form = cardQuote.closest('form');
    const open = (title, link, kind) => {
      if (title !== undefined) {
        cardQuote.querySelector('[data-bz-quote-title]').textContent = title;
        const product = cardQuote.querySelector('[data-bz-quote-product]');
        const url = cardQuote.querySelector('[data-bz-quote-link]');
        if (product) product.value = title;
        if (url) url.value = link || '';
        // "Reserve yours" (priced product, enquiries-only mode) vs "Request a quote" (price on request)
        cardQuote.dataset.kind = kind === 'reserve' ? 'reserve' : 'quote';
        cardQuote.querySelectorAll('[data-bz-quote-eyebrow], [data-bz-quote-fine], [data-bz-quote-swap]').forEach((el) => {
          el.textContent = el.dataset[cardQuote.dataset.kind] || el.textContent;
        });
        // fields for one kind only: hidden and disabled otherwise, so a required WhatsApp never blocks a quote
        cardQuote.querySelectorAll('[data-bz-only]').forEach((el) => {
          const on = el.dataset.bzOnly === cardQuote.dataset.kind;
          el.hidden = !on;
          el.querySelectorAll('input, textarea').forEach((input) => { input.disabled = !on; });
        });
        const request = cardQuote.querySelector('[data-bz-quote-request]');
        if (request) request.value = kind === 'reserve' ? 'Waiting list' : 'Quote request';
      }
      if (!cardQuote.open) cardQuote.showModal();
    };
    document.addEventListener('click', (event) => {
      const btn = event.target instanceof Element ? event.target.closest('[data-bz-quote]') : null;
      if (btn) {
        event.preventDefault();
        event.stopPropagation();
        open(btn.dataset.title, btn.dataset.link, btn.dataset.kind);
        return;
      }
      if (event.target === cardQuote || (event.target instanceof Element && event.target.closest('[data-bz-card-quote] [data-close]'))) {
        cardQuote.close();
      }
    }, true);
    // After sending, the page reloads: reopen this form (thanks or errors) only if it was the one used
    form?.addEventListener('submit', () => {
      try {
        sessionStorage.setItem('bz-card-quote', JSON.stringify([
          cardQuote.querySelector('[data-bz-quote-title]').textContent,
          cardQuote.querySelector('[data-bz-quote-link]')?.value || '',
          cardQuote.dataset.kind || 'quote',
        ]));
      } catch {}
    });
    let sent = null;
    try {
      sent = sessionStorage.getItem('bz-card-quote');
      sessionStorage.removeItem('bz-card-quote');
    } catch {}
    if (sent !== null && cardQuote.querySelector('.bz-dialog__success, .bz-dialog__error')) {
      let title = '';
      let link = '';
      let kind = 'quote';
      try {
        [title, link, kind = 'quote'] = JSON.parse(sent);
      } catch {}
      open(title, link, kind);
    }
  }

  // Product comparison: "Compare" toggles → compare bar → /pages/compare (sections/bugiz-compare.liquid)
  const CMP_KEY = 'bz-compare';
  const CMP_MAX = 4;
  const cmpRead = () => {
    try {
      return JSON.parse(localStorage.getItem(CMP_KEY) || '[]').slice(0, CMP_MAX);
    } catch {
      return [];
    }
  };
  const cmpWrite = (list) => {
    try {
      localStorage.setItem(CMP_KEY, JSON.stringify(list));
    } catch {}
  };
  const cmpEsc = (t) => String(t ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const cmpBar = document.querySelector('[data-bz-cmpbar]');
  // Collapsed to a small pill: the visitor's choice for this visit, else collapsed on product pages and phones
  const CMP_COLLAPSE_KEY = 'bz-cmpbar-collapsed';
  const cmpCollapsedDefault = () =>
    !!document.querySelector('.product-information') || window.matchMedia('(max-width: 749px)').matches;
  const cmpCollapsed = () => {
    try {
      const saved = sessionStorage.getItem(CMP_COLLAPSE_KEY);
      if (saved !== null) return saved === '1';
    } catch {}
    return cmpCollapsedDefault();
  };
  const cmpSetCollapsed = (on) => {
    try {
      sessionStorage.setItem(CMP_COLLAPSE_KEY, on ? '1' : '0');
    } catch {}
  };

  const cmpSync = () => {
    const list = cmpRead();
    const handles = list.map((p) => p.h);
    for (const btn of document.querySelectorAll('[data-bz-compare-add]')) {
      btn.setAttribute('aria-pressed', String(handles.includes(btn.dataset.handle)));
    }
    if (!cmpBar) return;
    const onComparePage = !!document.querySelector('[data-bz-compare]');
    cmpBar.hidden = !list.length || onComparePage;
    const collapsed = cmpCollapsed();
    cmpBar.classList.toggle('is-collapsed', collapsed);
    cmpBar.querySelector('[data-bz-cmpbar-toggle]')?.setAttribute('aria-expanded', String(!collapsed));
    document.body.classList.toggle('bz-has-cmpbar', !cmpBar.hidden && !collapsed);
    cmpBar.querySelector('[data-bz-cmpbar-count]').textContent = `(${list.length}/${CMP_MAX})`;
    cmpBar.querySelector('[data-bz-cmpbar-list]').innerHTML = list
      .map(
        (p) =>
          `<li class="bz-cmpbar__item"><img src="${cmpEsc(p.i)}" alt="" width="44" height="44"><span>${cmpEsc(p.t)}</span><button type="button" data-bz-cmp-remove="${cmpEsc(p.h)}" aria-label="${cmpEsc(cmpBar.dataset.labelRemove)} ${cmpEsc(p.t)}">×</button></li>`
      )
      .join('');
    const go = cmpBar.querySelector('[data-bz-cmpbar-go]');
    go.href = `${cmpBar.dataset.url}?p=${handles.map(encodeURIComponent).join(',')}`;
    go.classList.toggle('is-disabled', list.length < 2);
    go.setAttribute('aria-disabled', String(list.length < 2));
  };

  document.addEventListener('click', (event) => {
    const el = event.target instanceof Element ? event.target : null;
    if (!el) return;
    const add = el.closest('[data-bz-compare-add]');
    if (add) {
      event.preventDefault();
      event.stopPropagation();
      let list = cmpRead();
      const msg = cmpBar?.querySelector('[data-bz-cmpbar-msg]');
      if (msg) msg.hidden = true;
      if (list.some((p) => p.h === add.dataset.handle)) list = list.filter((p) => p.h !== add.dataset.handle);
      else if (list.length >= CMP_MAX) {
        if (msg) {
          msg.textContent = cmpBar.dataset.labelMax;
          msg.hidden = false;
        }
      } else list.push({ h: add.dataset.handle, t: add.dataset.title, i: add.dataset.image });
      cmpWrite(list);
      cmpSync();
      return;
    }
    const remove = el.closest('[data-bz-cmp-remove]');
    if (remove) {
      cmpWrite(cmpRead().filter((p) => p.h !== remove.dataset.bzCmpRemove));
      cmpSync();
      renderCompare?.();
      return;
    }
    if (el.closest('[data-bz-cmpbar-toggle]')) {
      cmpSetCollapsed(!cmpCollapsed());
      cmpSync();
      return;
    }
    if (el.closest('[data-bz-cmpbar-clear]')) {
      cmpWrite([]);
      cmpSync();
      return;
    }
    const go = el.closest('[data-bz-cmpbar-go]');
    if (go && go.classList.contains('is-disabled')) event.preventDefault();
  }, true);
  window.addEventListener('storage', (e) => e.key === CMP_KEY && cmpSync());

  // Compare page
  const cmpPage = document.querySelector('[data-bz-compare]');
  let renderCompare = null;
  if (cmpPage) {
    const table = cmpPage.querySelector('[data-bz-compare-table]');
    const diffToggle = cmpPage.querySelector('[data-bz-compare-diff]');
    const L = cmpPage.dataset;
    const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    const fromUrl = new URLSearchParams(location.search).get('p');
    if (fromUrl) {
      // the link's picks become the current comparison (keeps titles/images already stored for those)
      const known = cmpRead();
      cmpWrite(fromUrl.split(',').filter(Boolean).slice(0, CMP_MAX).map((h) => known.find((p) => p.h === h) || { h, t: h, i: '' }));
    }
    const cache = new Map();
    const load = (h) => {
      if (!cache.has(h)) cache.set(h, fetch(`${root}products/${encodeURIComponent(h)}?view=bz-compare`).then((r) => (r.ok ? r.json() : null)).catch(() => null));
      return cache.get(h);
    };
    renderCompare = async () => {
      const picks = cmpRead();
      if (!picks.length) {
        table.innerHTML = `<p class="bz-compare__note">${cmpEsc(L.labelEmpty)}</p>`;
        return;
      }
      const products = (await Promise.all(picks.map((p) => load(p.h)))).filter(Boolean);
      if (!products.length) {
        table.innerHTML = `<p class="bz-compare__note">${cmpEsc(L.labelEmpty)}</p>`;
        return;
      }
      // rows: key specs by label, then every spec-sheet row grouped (first-seen order across products)
      const groups = new Map();
      for (const p of products) {
        for (const r of p.specs || []) {
          const g = r.group || '';
          if (!groups.has(g)) groups.set(g, []);
          if (!groups.get(g).includes(r.label)) groups.get(g).push(r.label);
        }
      }
      const valueOf = (p, g, label) => (p.specs || []).find((r) => (r.group || '') === g && r.label === label)?.value ?? '—';
      // key specs that mean the same thing under different names share one row
      const KEY_ALIAS = {
        'main camera': 'Camera',
        'cámara principal': 'Cámara',
        'flight time': 'Max flight time',
        'tiempo de vuelo': 'Tiempo máximo de vuelo',
        'takeoff weight': 'Weight',
        'peso de despegue': 'Peso',
        'energy / voltage': 'Voltage / energy',
        'energía / voltaje': 'Voltaje / energía',
      };
      const keyName = (label) => KEY_ALIAS[String(label).toLowerCase()] || label;
      const keyLabels = [];
      for (const p of products) for (const k of p.key || []) if (!keyLabels.includes(keyName(k.label))) keyLabels.push(keyName(k.label));
      const keyOf = (p, label) => (p.key || []).find((k) => keyName(k.label) === label)?.value ?? '—';
      const cols = products.length;
      const row = (label, values) => {
        const same = values.every((v) => v === values[0]);
        return `<tr class="${same ? 'is-same' : 'is-diff'}"><th scope="row">${cmpEsc(label)}</th>${values.map((v) => `<td>${cmpEsc(v)}</td>`).join('')}</tr>`;
      };
      let html = `<div class="bz-compare__scroll"><table class="bz-compare__grid" style="--cols:${cols}"><thead><tr><td></td>`;
      for (const p of products) {
        html += `<th scope="col"><div class="bz-compare__prod"><button type="button" class="bz-compare__remove" data-bz-cmp-remove="${cmpEsc(p.handle)}" aria-label="${cmpEsc(L.labelRemove)} ${cmpEsc(p.title)}">×</button><a href="${cmpEsc(p.url)}" class="bz-compare__img">${p.image ? `<img src="${cmpEsc(p.image)}" alt="" loading="lazy">` : ''}</a><a href="${cmpEsc(p.url)}" class="bz-compare__name">${cmpEsc(p.title)}</a><span class="bz-compare__price">${cmpEsc(p.price)}</span>${p.quote ? `<button type="button" class="bz-compare__btn" data-bz-quote data-title="${cmpEsc(p.title)}" data-link="${cmpEsc(location.origin + p.url)}">${cmpEsc(L.labelQuote)}</button>` : `<a class="bz-compare__btn" href="${cmpEsc(p.url)}">${cmpEsc(L.labelView)}</a>`}</div></th>`;
      }
      html += '</tr></thead><tbody>';
      if (keyLabels.length) {
        html += `<tr class="bz-compare__group"><th colspan="${cols + 1}">${cmpEsc(L.labelKey)}</th></tr>`;
        for (const label of keyLabels) html += row(label, products.map((p) => keyOf(p, label)));
      }
      for (const [g, labels] of groups) {
        html += `<tr class="bz-compare__group"><th colspan="${cols + 1}">${cmpEsc(g)}</th></tr>`;
        for (const label of labels) html += row(label, products.map((p) => valueOf(p, g, label)));
      }
      html += '</tbody></table></div>';
      table.innerHTML = html;
      table.classList.toggle('is-diff-only', diffToggle.checked);
      // keep the stored titles/images in step with the real data (for the bar elsewhere)
      cmpWrite(picks.map((p) => {
        const d = products.find((x) => x.handle === p.h);
        return d ? { h: d.handle, t: d.title, i: d.image } : p;
      }));
    };
    diffToggle?.addEventListener('change', () => table.classList.toggle('is-diff-only', diffToggle.checked));
    renderCompare();
  }
  cmpSync();

  // Spec table: "Show all specifications"
  document.addEventListener('click', (event) => {
    const btn = event.target instanceof Element ? event.target.closest('[data-bz-specs-toggle]') : null;
    if (!btn) return;
    const groups = btn.parentElement.querySelector('[data-bz-specs]');
    const open = !groups.classList.contains('is-open');
    groups.classList.toggle('is-open', open);
    btn.setAttribute('aria-expanded', String(open));
    btn.querySelector('[data-more]').hidden = open;
    btn.querySelector('[data-less]').hidden = !open;
  });

  // Back to top
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-back-to-top]') : null;
    if (!button) return;
    (getScroller() || window).scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
  });

  // FAQ: one answer open at a time. <details name> handles this natively; this covers older browsers.
  document.addEventListener(
    'toggle',
    (event) => {
      const item = event.target;
      if (!(item instanceof HTMLDetailsElement) || !item.open || !item.classList.contains('bz-faq__item')) return;
      const group = item.getAttribute('name');
      for (const other of document.querySelectorAll(`details.bz-faq__item[name="${group}"][open]`)) {
        if (other !== item) other.open = false;
      }
    },
    true
  );

  // Cookie banner (snippets/bugiz-cookie-banner.liquid): choices go to Shopify's Customer Privacy API, which the
  // store's pixels and analytics follow. Shown until the visitor chooses; [data-bz-cookie-open] reopens it.
  (function bzCookies() {
    const banner = document.querySelector('[data-bz-cookie]');
    if (!banner) return;
    const KINDS = ['analytics', 'marketing', 'preferences'];
    const prefs = banner.querySelector('[data-bz-cookie-prefs]');
    const customize = banner.querySelector('[data-bz-cookie-customize]');
    const save = banner.querySelector('[data-bz-cookie-save]');
    const toggles = Object.fromEntries(
      KINDS.map((kind) => [kind, banner.querySelector(`[data-bz-cookie-kind="${kind}"]`)])
    );
    let privacy = null;

    const withPrivacy = (callback) => {
      if (privacy) return callback(privacy);
      if (window.Shopify?.customerPrivacy) return callback((privacy = window.Shopify.customerPrivacy));
      window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error && window.Shopify.customerPrivacy) callback((privacy = window.Shopify.customerPrivacy));
      });
    };

    const showPrefs = (show) => {
      prefs.hidden = !show;
      customize.hidden = show;
      save.hidden = !show;
    };

    const open = (withPrefs = false) => {
      const consent = privacy?.currentVisitorConsent?.() || {};
      for (const kind of KINDS) toggles[kind].checked = consent[kind] === 'yes';
      showPrefs(withPrefs);
      banner.hidden = false;
      requestAnimationFrame(() => banner.classList.add('is-open'));
    };

    const close = () => {
      banner.classList.remove('is-open');
      setTimeout(() => (banner.hidden = true), reducedMotion ? 0 : 300);
    };

    const choose = (choice) =>
      withPrivacy((api) => {
        api.setTrackingConsent({ ...choice, sale_of_data: choice.marketing }, () => {});
        close();
      });

    banner.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('[data-bz-cookie-accept]')) choose({ analytics: true, marketing: true, preferences: true });
      else if (target.closest('[data-bz-cookie-reject]')) choose({ analytics: false, marketing: false, preferences: false });
      else if (target.closest('[data-bz-cookie-customize]')) showPrefs(true);
      else if (target.closest('[data-bz-cookie-save]')) {
        choose(Object.fromEntries(KINDS.map((kind) => [kind, toggles[kind].checked])));
      }
    });

    document.addEventListener('click', (event) => {
      const opener = event.target instanceof Element ? event.target.closest('[data-bz-cookie-open]') : null;
      if (!opener) return;
      event.preventDefault();
      withPrivacy(() => open(true));
    });

    withPrivacy((api) => {
      const consent = api.currentVisitorConsent?.() || {};
      if (KINDS.every((kind) => !consent[kind])) open();
    });
  })();

  // Theme editor re-renders sections in place
  document.addEventListener('shopify:section:load', (event) => observeReveals(event.target));
})();
